import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx'; 
import { 
  ArrowLeft, 
  FileText, 
  Download, 
  Search, 
  Filter,
  Calendar,
  User,
  FileSpreadsheet,
  ShieldCheck // Nouvelle icône pour le validateur
} from 'lucide-react';

interface ArchivedSheet {
  id: string;
  year: number;
  week_number: number;
  status: string;
  pdf_url: string | null;
  signed_at: string;
  validated_at: string | null; // NOUVEAU
  employee: { full_name: string } | null;
  validator: { full_name: string } | null; // NOUVEAU
}

export default function AdminDocuments() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<ArchivedSheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [weekFilter, setWeekFilter] = useState<string>('all');

  useEffect(() => {
    const fetchDocuments = async () => {
      setLoading(true);
      try {
        // MODIFICATION ICI : On récupère l'employé ET le validateur
        // On utilise la syntaxe !colonne pour préciser la clé étrangère
        let query = supabase
          .from('weekly_sheets')
          .select('*, employee:employees!employee_id(full_name), validator:employees!validated_by(full_name)')
          .eq('status', 'validated')
          .eq('year', yearFilter);

        if (weekFilter !== 'all') {
          query = query.eq('week_number', parseInt(weekFilter));
        }

        const { data, error } = await query.order('week_number', { ascending: false });

        if (error) throw error;
        setDocuments(data as unknown as ArchivedSheet[]);
      } catch (err) {
        console.error("Erreur chargement archives:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [yearFilter, weekFilter]);

  const filteredDocs = documents.filter(doc => 
    doc.employee?.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- FONCTION EXPORT EXCEL ---
  const exportExcel = () => {
    const dataToExport = filteredDocs.map(doc => ({
      'Employé': doc.employee?.full_name || 'Inconnu',
      'Année': doc.year,
      'Semaine': doc.week_number,
      'Date Signature': new Date(doc.signed_at).toLocaleDateString(),
      'Validé par': doc.validator?.full_name || 'Non tracé', // AJOUT EXCEL
      'Date Validation': doc.validated_at ? new Date(doc.validated_at).toLocaleDateString() : '-',
      'Lien Document': doc.pdf_url || 'Non disponible'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Archives");

    // Ajustement largeur colonnes
    const wscols = [
      { wch: 25 }, // Employé
      { wch: 10 }, // Année
      { wch: 10 }, // Semaine
      { wch: 20 }, // Date Sign
      { wch: 25 }, // Validé par (NOUVEAU)
      { wch: 20 }, // Date Valid (NOUVEAU)
      { wch: 50 }, // Lien
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, `archives_${yearFilter}_${weekFilter === 'all' ? 'toutes' : 'S'+weekFilter}.xlsx`);
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      
      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/')} 
          style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px', cursor: 'pointer', color:'#64748b' }}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>Archives & Documents</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Historique des feuilles signées</p>
        </div>
      </div>

      {/* BARRE D'OUTILS */}
      <div style={{ background: 'white', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '24px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems:'center' }}>
        
        {/* Recherche Nom */}
        <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
          <Search size={20} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input 
            type="text" 
            placeholder="Rechercher un employé..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '12px 12px 12px 40px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem', outline: 'none' }}
          />
        </div>

        {/* Filtres & Actions */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems:'center' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '0 15px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <Calendar size={18} color="#64748b" />
            <select 
              value={weekFilter} 
              onChange={(e) => setWeekFilter(e.target.value)}
              style={{ background: 'transparent', border: 'none', fontSize: '0.95rem', color: '#334155', padding: '12px 0', cursor: 'pointer', outline: 'none', fontWeight: '600', minWidth: '130px' }}
            >
              <option value="all">Toutes semaines</option>
              {Array.from({ length: 53 }, (_, i) => i + 1).map(w => (
                <option key={w} value={w}>Semaine {w}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '0 15px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <Filter size={18} color="#64748b" />
            <select 
              value={yearFilter} 
              onChange={(e) => setYearFilter(parseInt(e.target.value))}
              style={{ background: 'transparent', border: 'none', fontSize: '0.95rem', color: '#334155', padding: '12px 0', cursor: 'pointer', outline: 'none', fontWeight: '600' }}
            >
              <option value={2024}>Année 2024</option>
              <option value={2025}>Année 2025</option>
              <option value={2026}>Année 2026</option>
            </select>
          </div>

          <button 
            onClick={exportExcel}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '8px', 
              background: '#10b981', color: 'white', border: 'none', 
              padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', 
              fontWeight: '600', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
            }}
          >
            <FileSpreadsheet size={18} /> Excel
          </button>

        </div>
      </div>

      {/* LISTE DOCUMENTS */}
      {loading ? (
        <p style={{ textAlign: 'center', color: '#64748b', marginTop: '40px' }}>Chargement des archives...</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          
          {filteredDocs.length === 0 ? (
             <div style={{ gridColumn: '1 / -1', padding: '60px', background: 'white', borderRadius: '16px', textAlign: 'center', border: '1px dashed #cbd5e1', color: '#64748b' }}>
               <FileText size={48} style={{ marginBottom: '15px', opacity: 0.3 }} />
               <p style={{ fontWeight: '500' }}>Aucun document trouvé.</p>
             </div>
          ) : (
            filteredDocs.map(doc => (
              <div 
                key={doc.id} 
                style={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '16px', 
                  padding: '20px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  transition: 'transform 0.2s'
                }}
              >
                {/* En-tête Carte */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '10px', color: '#2563eb', height: 'fit-content' }}>
                      <User size={20} />
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '1rem' }}>{doc.employee?.full_name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: '#64748b', marginTop: '2px' }}>
                        <Calendar size={14} /> Semaine {doc.week_number} • {doc.year}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section Validateur (NOUVEAU) */}
                <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#334155', fontWeight: '600', marginBottom: '2px' }}>
                    <ShieldCheck size={14} color="#10b981" />
                    Validé par {doc.validator?.full_name || '...'}
                  </div>
                  <div style={{ color: '#94a3b8', paddingLeft: '20px' }}>
                    {doc.validated_at ? new Date(doc.validated_at).toLocaleDateString() : 'Date inconnue'}
                  </div>
                </div>

                <div style={{ height: '1px', background: '#f1f5f9' }}></div>

                {/* Footer Carte */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    Signé le {new Date(doc.signed_at).toLocaleDateString()}
                  </span>
                  
                  {doc.pdf_url ? (
                    <a 
                      href={doc.pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'flex', alignItems: 'center', gap: '6px', 
                        padding: '8px 12px', background: '#f0fdf4', 
                        color: '#15803d', borderRadius: '8px', 
                        textDecoration: 'none', fontWeight: '600', fontSize: '0.85rem',
                        border: '1px solid #bbf7d0', transition: 'background 0.2s'
                      }}
                    >
                      <Download size={16} /> PDF Signé
                    </a>
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: '#ef4444', fontStyle: 'italic', display: 'flex', alignItems:'center', gap:'4px' }}>
                      <FileText size={14} /> Fichier introuvable
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}